import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
  useParams,
} from "react-router-dom";
import "./styles/Form.css";
import "./styles/FilterSystem.css";
import ProtectedRoute from "./ProtectedRoute"; // Import your new component
import GlobalPermissionGuard from "./components/GlobalPermissionGuard";
import Login from "./pages/Login/Login";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import AdminUserPage from "./pages/Admin/AdminUserPage";
import AdminGroupPage from "./pages/Admin/AdminGroupPage";
import EditGroupAdmin from "./pages/Admin/EditGroupAdmin";
import UserForm from "./pages/Admin/UserForm";
import EditUserAdmin from "./pages/Admin/EditUserAdmin";
import EditUserSelf from "./pages/User/EditUserSelf";
import MyAccountPage from "./pages/User/MyAccountPage";
import UserHome from "./pages/User/UserHome";
import WorkHistoryPage from "./pages/User/WorkHistoryPage";
import AppLayout from "./components/AppLayout";
import DriverAbstractList from "./pages/User/DriverAbstractList";
import DriverAbstractForm from "./pages/User/DriverAbstractForm";
import RoadTestList from "./pages/User/RoadTestList";
import RoadTestForm from "./pages/User/RoadTestForm";
import RoadsideInspectionList from "./pages/User/RoadsideInspectionList";
import RoadsideInspectionForm from "./pages/User/RoadsideInspectionForm";
import DriverLicense from "./pages/User/DriverLicense";
import DriverLicenseForm from "./pages/User/DriverLicenseForm";
import VacationForm from "./pages/User/VacationForm";
import EmergencyContact from "./pages/User/EmergencyContact";
import CanadaPost from "./pages/User/CanadaPost";
import IncidentList from "./pages/User/IncidentList";
import IncidentForm from "./pages/User/IncidentForm";
import ViolationList from "./pages/User/ViolationList";
import ViolationForm from "./pages/User/ViolationForm";
import AnnualDriverCertificationList from "./pages/User/AnnualDriverCertificationList";
import AnnualDriverCertificationForm from "./pages/User/AnnualDriverCertificationForm";
import DriverDisclosureList from "./pages/User/DriverDisclosureList";
import DriverDisclosureForm from "./pages/User/DriverDisclosureForm";
import ForgotPassword from "./pages/Login/ForgotPassword";
import ResetPassword from "./pages/Login/ResetPassword";
import DangerousWorkPolicy from "./pages/Policies/DangerousWorkPolicy";
import ZeroTolerancePolicy from "./pages/Policies/ZeroTolerancePolicy";
import TruckAssignmentPolicy from "./pages/Policies/TruckAssignmentPolicy";
import ClearinghouseConsent from "./pages/Policies/ClearinghouseConsent";
import HealthandSafetyManual from "./pages/Policies/HealthandSafetyManual";
import PadlockProbePolicy from "./pages/Policies/PadlockProbePolicy";
import BackgroundCheckFCRA from "./pages/User/BackgroundCheckFCRA";
import QualificationsPage from "./pages/User/QualificationsPage";
import DriverEmployeeAgreement from "./pages/Policies/DriverEmployeeAgreement";
import TaxForm from "./pages/User/TaxForm";
import BackgroundCheck from "./pages/Policies/BackgroundCheck";
import ListAllConsentHistory from "./pages/Admin/ListAllConsentHistory";
import ConsentHistoryPage from "./pages/Policies/ConsentHistoryPage";
import SlideWrapper from "./components/SlideWrapper";
import SignedRelease from "./pages/Admin/SignedRelease";
import GatePassPolicy from "./pages/Policies/GatePassPolicy";
import LEEP from "./pages/Policies/LEEP";
import DirectDeposit from "./pages/User/DirectDeposit";
import JobDescriptionPage from "./pages/Admin/JobDescriptionPage";
import PositionPage from "./pages/Admin/PositionPage";
import ReportListPage from "./pages/Reports/ReportListPage";
import ContactListReport from "./pages/Reports/ContactListReport";
import LeepResponsesReport from "./pages/Reports/LeepResponsesReport";
import TimeAndAttendanceReport from "./pages/Reports/TimeAndAttendanceReport";
import ExpirationsReport from "./pages/Reports/ExpirationsReport";
import AssociateTurnoverReport from "./pages/Reports/AssociateTurnoverReport";
import HiringCycleReport from "./pages/Reports/HiringCycleReport";
import HiringCycleData from "./pages/Reports/HiringCycleData";
import PositionHistoryReport from "./pages/Reports/PositionHistoryReport";
import TerminationReport from "./pages/Reports/TerminationReport";
import DocumentStatusReport from "./pages/Reports/DocumentStatusReport";
import IncidentAccidentRegistry from "./pages/Reports/IncidentAccidentRegistry";
import TagEditorPage from "./pages/Admin/TagEditorPage";
import AccountSettingsPage from "./pages/Admin/AccountSettingsPage";
import ApiDocumentationPage from "./pages/Admin/ApiDocumentationPage";
import CommunicationsPage, { ComposeEmailPage } from "./pages/Admin/CommunicationsPage";
import TrainingPanel from "./pages/User/TrainingPanel";
import InterviewQuestionsPage from "./pages/User/InterviewQuestionsPage";
import StatusHistoryPage from "./pages/Admin/StatusHistoryPage";
import ApplicationHistoryPage from "./pages/Admin/ApplicationHistoryPage";
import PermissionManager from "./pages/Admin/PermissionManager";
import CustomTaskPage from "./pages/Policies/CustomTaskPage";
import ReferenceCheckFormPage from "./pages/Public/ReferenceCheckFormPage";
import ReferenceCheckResultPage from "./pages/User/ReferenceCheckResultPage";

function ScrollToTop() {
  const location = useLocation();

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  return null;
}

function LegacyWorkPermitRedirect() {
  const { id, wpId } = useParams();

  if (wpId) {
    return <Navigate to={`/task/work_permit/${id}/${wpId}`} replace />;
  }

  return <Navigate to={`/task/work_permit/${id}`} replace />;
}

function LegacyWorkPermitNewRedirect() {
  const { id } = useParams();
  return <Navigate to={`/task/work_permit/${id}/new`} replace />;
}

function LegacyResumeRedirect() {
  const { id } = useParams();
  return <Navigate to={`/task/resume/${id}`} replace />;
}

function AppRoutes() {
  const location = useLocation();
  const backgroundLocation = location.state?.backgroundLocation;

  return (
    <>
      <Routes location={backgroundLocation || location}>
        <Route path="/" element={<Login />} />

        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route
          path="/reference-check/:token"
          element={<ReferenceCheckFormPage />}
        />

        {/* Protected Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <AdminDashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-users"
          element={
            <ProtectedRoute
              adminOnly={true}
              page="AdminUser"
              resource="User"
              action="view"
            >
              <AppLayout>
                <AdminUserPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-groups"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <AdminGroupPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-groups/:groupId"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <EditGroupAdmin />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/form"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <UserForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/edit-user/:id"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <EditUserAdmin />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/my-account"
          element={
            <ProtectedRoute>
              <AppLayout>
                <MyAccountPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <AppLayout>
                <EditUserSelf />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/userhome"
          element={
            <ProtectedRoute>
              <AppLayout>
                <UserHome />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/userhome/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <UserHome />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/work-history/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <WorkHistoryPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/edit-workhistory/:id"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <WorkHistoryPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reference-check-results/:checkId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ReferenceCheckResultPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Work Permit Routes */}
        <Route
          path="/workpermit/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <LegacyWorkPermitRedirect />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/workpermit/:id/detail/:wpId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <LegacyWorkPermitRedirect />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/edit-workpermit/:id/detail/:wpId"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <LegacyWorkPermitRedirect />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/workpermit/:id/new"
          element={
            <ProtectedRoute>
              <AppLayout>
                <LegacyWorkPermitNewRedirect />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Road Test Routes */}
        <Route
          path="/roadtest/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <RoadTestList />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/roadtest/:id/detail/:rtId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <RoadTestForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/roadtest/:id/new"
          element={
            <ProtectedRoute>
              <AppLayout>
                <RoadTestForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/roadside/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <RoadsideInspectionList />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/roadside/:id/detail/:riId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <RoadsideInspectionForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/roadside/:id/new"
          element={
            <ProtectedRoute>
              <AppLayout>
                <RoadsideInspectionForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/driverlicense/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DriverLicense />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/driverabstract/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DriverAbstractList />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/driverabstract/:id/detail/:daId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DriverAbstractForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/driverabstract/:id/new"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DriverAbstractForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/driverlicense/:id/new"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DriverLicenseForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/driverlicense/:id/detail/:dlId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DriverLicenseForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/edit-driver/:id"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <DriverLicense />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/vacationform/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <VacationForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/edit-vacation/:id"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <VacationForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/emergencycontact/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <EmergencyContact />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/edit-emergency/:id"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <EmergencyContact />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/canadapost/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <CanadaPost />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/edit-canadapost/:id"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <CanadaPost />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/incidents/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <IncidentList />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/incidents/:id/incident-detail/:incidentId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <IncidentForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/incidents/:id/incident-new"
          element={
            <ProtectedRoute>
              <AppLayout>
                <IncidentForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/violations/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ViolationList />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/violations/:id/violation-detail/:violId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ViolationForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/violations/:id/violation-new"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ViolationForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/annual-driver-certifications/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <AnnualDriverCertificationList />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/annual-driver-certifications/:id/detail/:adcId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <AnnualDriverCertificationForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/annual-driver-certifications/:id/new"
          element={
            <ProtectedRoute>
              <AppLayout>
                <AnnualDriverCertificationForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/driver-disclosure/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DriverDisclosureList />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/driver-disclosure/:id/detail/:ddId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DriverDisclosureForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/driver-disclosure/:id/new"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DriverDisclosureForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/directdeposit/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DirectDeposit />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/edit-directdeposit/:id"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <DirectDeposit />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dangerouswork/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DangerousWorkPolicy />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/task/:t_key/:userId?"
          element={
            <ProtectedRoute>
              <AppLayout>
                <CustomTaskPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/task/:t_key/:userId/detail/:recordId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <CustomTaskPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/task/:t_key/:userId/new"
          element={
            <ProtectedRoute>
              <AppLayout>
                <CustomTaskPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/slide/task/:t_key/:userId?"
          element={
            <ProtectedRoute>
              <SlideWrapper>
                <CustomTaskPage />
              </SlideWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path="/zerotolerance/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ZeroTolerancePolicy />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/truckassignment/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <TruckAssignmentPolicy />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/healthandsafety/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <HealthandSafetyManual />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/padlockpolicy/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <PadlockProbePolicy />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/background-check-fcra/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <BackgroundCheckFCRA />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/slide/background-check-fcra/:id"
          element={
            <ProtectedRoute>
              <SlideWrapper>
                <BackgroundCheckFCRA />
              </SlideWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/qualifications/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <QualificationsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/driver-agreement/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DriverEmployeeAgreement />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/taxform/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <TaxForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/gatepasspolicy/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <GatePassPolicy />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/resume/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <LegacyResumeRedirect />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/backgroundcheck/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <BackgroundCheck />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/leep/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <LEEP />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/consent/:id/:whId?"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ConsentHistoryPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/consent/list/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ListAllConsentHistory />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/slide/backgroundcheck/:id"
          element={
            <ProtectedRoute>
              <SlideWrapper>
                <BackgroundCheck />
              </SlideWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path="/slide/consent/:id/:whId"
          element={
            <ProtectedRoute>
              <SlideWrapper>
                <ConsentHistoryPage />
              </SlideWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path="/signed-release/:id/:whId?"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SignedRelease />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/job-descriptions"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <JobDescriptionPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/positions"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <PositionPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <ReportListPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports/contact-list"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <ContactListReport />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports/leep-report"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <LeepResponsesReport />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports/time-and-attendance"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <TimeAndAttendanceReport />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports/expirations"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <ExpirationsReport />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports/associate-turnover"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <AssociateTurnoverReport />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports/hiring-cycle"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <HiringCycleReport />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports/hiring-cycle-data"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <HiringCycleData />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports/position-history"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <PositionHistoryReport />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports/termination"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <TerminationReport />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports/document-status"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <DocumentStatusReport />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports/incident-accident-registry"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <IncidentAccidentRegistry />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/tags/:userId"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <TagEditorPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/account-setting"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <AccountSettingsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/api-documentation"
          element={<ApiDocumentationPage />}
        />

        <Route
          path="/communications"
          element={<ProtectedRoute adminOnly={true}><AppLayout><CommunicationsPage /></AppLayout></ProtectedRoute>}
        />
        <Route
          path="/communications/compose"
          element={<ProtectedRoute adminOnly={true}><AppLayout><ComposeEmailPage /></AppLayout></ProtectedRoute>}
        />

        <Route
          path="/training/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <TrainingPanel />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/interview/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <InterviewQuestionsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/clearinghouse/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ClearinghouseConsent />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/status-history/:id"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <StatusHistoryPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/application-history/:id"
          element={
            <ProtectedRoute adminOnly={true}>
              <AppLayout>
                <ApplicationHistoryPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/permissions"
          element={
            <ProtectedRoute
              adminOnly={true}
              page="PermissionManagerPage"
              resource="Permissions"
              action="view"
            >
              <AppLayout>
                <PermissionManager />
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Routes>

      {backgroundLocation && (
        <Routes>
          <Route
            path="/slide/task/:t_key/:userId?"
            element={
              <ProtectedRoute>
                <SlideWrapper>
                  <CustomTaskPage />
                </SlideWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/slide/background-check-fcra/:id"
            element={
              <ProtectedRoute>
                <SlideWrapper>
                  <BackgroundCheckFCRA />
                </SlideWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/slide/backgroundcheck/:id"
            element={
              <ProtectedRoute>
                <SlideWrapper>
                  <BackgroundCheck />
                </SlideWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/slide/consent/:id/:whId"
            element={
              <ProtectedRoute>
                <SlideWrapper>
                  <ConsentHistoryPage />
                </SlideWrapper>
              </ProtectedRoute>
            }
          />
        </Routes>
      )}
    </>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <GlobalPermissionGuard />
      <AppRoutes />
    </Router>
  );
}

export default App;
